// src/components/FloodMap.jsx 
import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
  Polyline,
  CircleMarker,
  Polygon, 
} from "react-leaflet";

import { Info, Layers } from "lucide-react"; 
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNWSAlerts } from "../services/nwsWeatherService"; 


import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});


const BOROUGH_BOUNDARIES = {
  brooklyn: [
    [40.5707, -74.0494], // Southwest
    [40.5707, -73.8333], // Southeast  
    [40.7394, -73.8333], // Northeast
    [40.7394, -74.0494], // Northwest
  ],
  queens: [
    [40.5412, -73.8333], // Southwest
    [40.5412, -73.7004], // Southeast
    [40.8000, -73.7004], // Northeast  
    [40.8000, -73.8333], // Northwest
  ]
};


const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; 
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; 
  return distance;
};


const findNearestSensor = (clickedLatLng, sensors) => {
  if (!sensors || sensors.length === 0) return null;

  let nearestSensor = null;
  let minDistance = Infinity;

  sensors.forEach((sensor) => {
    if (!sensor.coordinates || sensor.coordinates.length !== 2) return;

    const distance = calculateDistance(
      clickedLatLng.lat,
      clickedLatLng.lng,
      sensor.coordinates[0],
      sensor.coordinates[1]
    );

    if (distance < minDistance) {
      minDistance = distance;
      nearestSensor = sensor;
    }
  });

  return nearestSensor;
};


const MapClickHandler = ({
  sensors,
  onSensorSelect,
  setClickedLocation,
  setNearestSensorLine,
  setStatusMessage,
}) => {
  useMapEvents({
    click: (e) => {

      if (
        e.originalEvent.target.closest(".leaflet-marker-icon") ||
        e.originalEvent.target.closest(".custom-sensor-icon")
      ) {
        return; 
      }

      const clickedLatLng = e.latlng;
      console.log("Map clicked at:", clickedLatLng);


      setClickedLocation(clickedLatLng);


      const nearestSensor = findNearestSensor(clickedLatLng, sensors);

      if (nearestSensor) {
        const distance = calculateDistance(
          clickedLatLng.lat,
          clickedLatLng.lng,
          nearestSensor.coordinates[0],
          nearestSensor.coordinates[1]
        );

        console.log(
          `Nearest sensor: ${nearestSensor.id} at ${distance.toFixed(2)}km away`
        );


        if (distance <= 3) {
          onSensorSelect(nearestSensor);


          setNearestSensorLine({
            from: [clickedLatLng.lat, clickedLatLng.lng],
            to: nearestSensor.coordinates,
            distance: distance,
          });

          setStatusMessage({
            type: "success",
            text: `Found sensor ${distance.toFixed(2)}km away`,
          });


          setTimeout(() => {
            setNearestSensorLine(null);
            setClickedLocation(null);
            setStatusMessage(null);
          }, 3000);
        } else {
          console.log("Clicked location is too far from any sensor");
          setNearestSensorLine(null);
          setStatusMessage({
            type: "warning",
            text: `Nearest sensor is ${distance.toFixed(2)}km away (too far)`,
          });


          setTimeout(() => {
            setClickedLocation(null);
            setStatusMessage(null);
          }, 2000);
        }
      } else {
        setNearestSensorLine(null);
        setStatusMessage({
          type: "error",
          text: "No sensors found in this area",
        });

        setTimeout(() => {
          setClickedLocation(null);
          setStatusMessage(null);
        }, 2000);
      }
    },
  });

  return null;
};


const WeatherAlertLayer = ({ alerts, showAlerts }) => {
  if (!showAlerts) return null;


  if (!alerts || alerts.length === 0) {
    return (
      <React.Fragment>

        <Polygon
          positions={BOROUGH_BOUNDARIES.brooklyn}
          pathOptions={{
            color: '#3b82f6',      
            fillColor: '#3b82f6',  
            fillOpacity: 0.05,   
            weight: 1,            
            opacity: 0.3,          
          }}
        />

        <Polygon
          positions={BOROUGH_BOUNDARIES.queens}
          pathOptions={{
            color: '#3b82f6',      
            fillColor: '#3b82f6',  
            fillOpacity: 0.05,     
            weight: 1,             
            opacity: 0.3,         
          }}
        />
      </React.Fragment>
    );
  }


  return (
    <>
      {alerts.map((alert, index) => {

        if (alert.geometry && alert.geometry.coordinates) {

        }
        

        const opacity = alert.event.includes('Warning') ? 0.3 : 0.2;
        
        return (
          <React.Fragment key={`alert-${index}`}>
            {/* Brooklyn区域 */}
            <Polygon
              positions={BOROUGH_BOUNDARIES.brooklyn}
              pathOptions={{
                color: alert.color,
                fillColor: alert.color,
                fillOpacity: opacity,
                weight: 2,
                opacity: 0.8,
              }}
            />
            {/* Queens区域 */}
            <Polygon
              positions={BOROUGH_BOUNDARIES.queens}
              pathOptions={{
                color: alert.color,
                fillColor: alert.color,
                fillOpacity: opacity,
                weight: 2,
                opacity: 0.8,
              }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};


const createSensorIcon = (severity, isActive = false, isHovered = false) => {
  const colors = {
    normal: {
      bg: "#3b82f6",
      border: "#1e40af", 
      shadow: "rgba(59, 130, 246, 0.4)",
      glow: "#3b82f6",
    },
    minor: { 
      bg: "#eab308",
      border: "#ca8a04",
      shadow: "rgba(234, 179, 8, 0.4)",
      glow: "#eab308",
    },
    mild: { 
      bg: "#eab308",
      border: "#ca8a04", 
      shadow: "rgba(234, 179, 8, 0.4)",
      glow: "#eab308",
    },
    moderate: { 
      bg: "#f97316", 
      border: "#ea580c", 
      shadow: "rgba(249, 115, 22, 0.4)",
      glow: "#f97316",
    },
    severe: { 
      bg: "#dc2626", // red-600
      border: "#991b1b", // red-800
      shadow: "rgba(220, 38, 38, 0.5)",
      glow: "#dc2626",
    },
    major: { // 🔧 兼容：支持新的NWS命名
      bg: "#dc2626",
      border: "#991b1b",
      shadow: "rgba(220, 38, 38, 0.5)",
      glow: "#dc2626",
    },
  };

  const color = colors[severity] || colors.normal;
  const baseSize = isActive ? 24 : 20;
  const size = isHovered ? baseSize + 4 : baseSize;


  const pulseRing =
    severity !== "normal"
      ? `
    <circle cx="16" cy="16" r="10" fill="none" stroke="${color.glow}" stroke-width="2" opacity="0.6">
      <animate attributeName="r" values="10;16;10" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
    </circle>
  `
      : "";

  const svgIcon = `
    <svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-${severity}" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="${
            color.shadow
          }"/>
        </filter>
        <radialGradient id="gradient-${severity}" cx="30%" cy="30%">
          <stop offset="0%" style="stop-color:rgba(255,255,255,0.4)"/>
          <stop offset="100%" style="stop-color:rgba(0,0,0,0.2)"/>
        </radialGradient>
      </defs>
      
      ${pulseRing}
      
      <!-- 主圆形 -->
      <circle cx="16" cy="16" r="12" fill="${color.bg}" stroke="${
    color.border
  }" 
              stroke-width="2" filter="url(#shadow-${severity})"/>
      
      <!-- 光泽效果 -->
      <circle cx="16" cy="16" r="12" fill="url(#gradient-${severity})"/>
      
      <!-- 内部水滴图标 -->
      <g transform="translate(16, 16) scale(0.6)">
        <path d="M0,-8 C-4,-4 -6,0 -6,3 C-6,6.3 -3.3,9 0,9 C3.3,9 6,6.3 6,3 C6,0 4,-4 0,-8 Z" 
              fill="white" opacity="0.9"/>
        <circle cx="0" cy="2" r="1.5" fill="${color.border}" opacity="0.8"/>
      </g>
      
      <!-- 选中状态的外环 -->
      ${
        isActive
          ? `<circle cx="16" cy="16" r="15" fill="none" stroke="white" stroke-width="3" opacity="0.8"/>`
          : ""
      }
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: "custom-sensor-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};


const MapController = ({ selectedSensor }) => {
  const map = useMap();


  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    };


    window.addEventListener("resize", handleResize);


    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, [map]);


  useEffect(() => {
    if (selectedSensor && selectedSensor.coordinates && selectedSensor.coordinates.length === 2) {
      const [lat, lng] = selectedSensor.coordinates;
      

      if (!isNaN(lat) && !isNaN(lng)) {
        console.log(`🗺️ Zooming to sensor ${selectedSensor.id} at [${lat}, ${lng}]`);
        
 
        setTimeout(() => {
          map.setView([lat, lng], 16, {
            animate: true,
            duration: 1.0, 
            easeLinearity: 0.25
          });
        }, 100); 
      } else {
        console.warn('Invalid sensor coordinates:', selectedSensor.coordinates);
      }
    }
  }, [selectedSensor, map]);

  return null;
};


const MapResizeObserver = () => {
  const map = useMap();
  const containerRef = useRef();

  useEffect(() => {
    if (!containerRef.current && map.getContainer()) {
      containerRef.current = map.getContainer().parentElement;
    }

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        map.invalidateSize();
      }, 50);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [map]);

  return null;
};


const FloodMap = ({ sensors, selectedSensor, onSensorSelect }) => {
  const [mapReady, setMapReady] = useState(false);
  const [hoveredSensor, setHoveredSensor] = useState(null);
  const [clickedLocation, setClickedLocation] = useState(null);
  const [nearestSensorLine, setNearestSensorLine] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [showWeatherAlerts, setShowWeatherAlerts] = useState(true); 
  const mapContainerRef = useRef(null);


  const { alerts, riskLevel, riskLevelInfo } = useNWSAlerts();


  const nycCenter = [40.7128, -74.006];

const getSensorSeverity = (sensor) => {
  if (sensor.latestEvent) {

    const severity = sensor.latestEvent.severity?.toLowerCase();
    

    const severityMapping = {
      'mild': 'minor',     
      'minor': 'minor',    
      'moderate': 'moderate', 
      'severe': 'severe',  
      'major': 'severe'    
    };
    
    return severityMapping[severity] || 'normal';
  }
  return "normal";
};


  useEffect(() => {
    if (mapReady && mapContainerRef.current) {
      const timer = setTimeout(() => {

        window.dispatchEvent(new Event("resize"));
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [mapReady]);

  return (
    <div
      ref={mapContainerRef}
      className="w-full h-full relative overflow-hidden"
      style={{ minHeight: "300px" }}
    >
      {!mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-50">
          <div className="text-center p-6 bg-white rounded-lg shadow-lg border">
            <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
            <div className="text-sm font-medium text-gray-700">
              Loading Map...
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Initializing NYC flood sensors
            </div>
          </div>
        </div>
      )}

      <MapContainer
        center={nycCenter}
        zoom={11}
        style={{
          height: "100%",
          width: "100%",
          minHeight: "100%",
        }}
        className="leaflet-container"
        whenReady={() => {
          console.log("Map ready");
          setMapReady(true);
        }}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        attributionControl={true}
        zoomControl={true}
        preferCanvas={false}
      >

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={19}
          tileSize={256}
          detectRetina={true}
        />

        
        <WeatherAlertLayer 
          alerts={alerts} 
          showAlerts={showWeatherAlerts} 
        />

        
        <MapClickHandler
          sensors={sensors}
          onSensorSelect={onSensorSelect}
          setClickedLocation={setClickedLocation}
          setNearestSensorLine={setNearestSensorLine}
          setStatusMessage={setStatusMessage}
        />

        
        {clickedLocation && (
          <>
            
            <CircleMarker
              center={[clickedLocation.lat, clickedLocation.lng]}
              radius={15}
              pathOptions={{
                color: "#3b82f6",
                fillColor: "transparent",
                fillOpacity: 0,
                weight: 2,
                opacity: 0.6,
                dashArray: "5, 5",
              }}
            />
           
            <CircleMarker
              center={[clickedLocation.lat, clickedLocation.lng]}
              radius={6}
              pathOptions={{
                color: "#1e40af",
                fillColor: "#3b82f6",
                fillOpacity: 0.8,
                weight: 2,
              }}
            />
          </>
        )}

       
        {nearestSensorLine && (
          <Polyline
            positions={[nearestSensorLine.from, nearestSensorLine.to]}
            pathOptions={{
              color: "#10b981",
              weight: 3,
              opacity: 0.8,
              dashArray: "10, 10",
            }}
          />
        )}

        
        {mapReady &&
          sensors.map((sensor) => {
            if (!sensor.coordinates || sensor.coordinates.length !== 2) {
              console.warn(
                `Invalid coordinates for sensor ${sensor.id}:`,
                sensor.coordinates
              );
              return null;
            }

            const severity = getSensorSeverity(sensor);
            const isSelected = selectedSensor?.id === sensor.id;
            const isHovered = hoveredSensor === sensor.id;

            return (
              <Marker
                key={sensor.id}
                position={sensor.coordinates}
                icon={createSensorIcon(severity, isSelected, isHovered)}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stopPropagation(e);
                    console.log("[Marker Click]", sensor.id, sensor.location);
                    onSensorSelect(sensor);
                  },
                  mouseover: (e) => {
                    L.DomEvent.stopPropagation(e);
                    setHoveredSensor(sensor.id);
                  },
                  mouseout: (e) => {
                    L.DomEvent.stopPropagation(e);
                    setHoveredSensor(null);
                  },
                }}
              />
            );
          })}

        
        <MapController selectedSensor={selectedSensor} />

       
        <MapResizeObserver />
      </MapContainer>

     
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-4 z-[1000] max-w-52">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-blue-500">📊</span>
          <h4 className="text-sm font-bold text-gray-800">Flood Levels</h4>
          <a 
            href="https://www.nssl.noaa.gov/education/svrwx101/floods/faq/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 transition-colors"
            title="NWS Flood Standards"
          >
            <Info className="h-3 w-3" />
          </a>
        </div>
        <div className="space-y-2">
          {[
            {
              level: "Minor",
              color: "#eab308",
              severity: "minor",
              desc: '0-6"',
              impact: "Vehicle safe"
            },
            { 
              level: "Moderate", 
              color: "#f97316", 
              severity: "moderate", 
              desc: '6-12"',
              impact: "Cars unsafe"
            },
            {
              level: "Major",
              color: "#dc2626",
              severity: "severe",
              desc: '12"+',
              impact: "All dangerous"
            },
          ].map(({ level, color, desc, impact }) => (
            <div key={level} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full border-2 shadow-sm flex-shrink-0"
                style={{
                  backgroundColor: color,
                  borderColor: color,
                  boxShadow: `0 2px 4px ${color}33`,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-700">
                  {level} Flooding
                </div>
                <div className="text-xs text-gray-500 flex justify-between">
                  <span>{desc}</span>
                  <span className="text-gray-400">{impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            <strong>NWS Standard</strong> + Vehicle Safety
          </div>
        </div>
      </div>

   
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-4 z-[1000]">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600 mb-1">
            {sensors.length}
          </div>
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Sensors with Events
          </div>
        </div>
      </div>

    
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-4 z-[1000] max-w-52">
        <div className="text-center">
          <div className="text-lg mb-2">🗺️</div>
          <div className="text-xs font-semibold text-gray-700 mb-1">
            Interactive Map
          </div>
          <div className="text-xs text-gray-500 mb-2">
            • Click sensors for details
            <br />
            • Click anywhere to find nearest sensor
            <br />• Auto-select within 3km range
          </div>
          
        
          <div className="mt-3 p-2 bg-gray-50 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Layers</span>
              <Layers className="h-3 w-3 text-gray-500" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={showWeatherAlerts}
                onChange={(e) => setShowWeatherAlerts(e.target.checked)}
                className="rounded"
              />
              <span>Weather Alerts</span>
            </label>
          </div>
          
        
          <div className="mt-3 p-2 rounded-lg border" style={{ 
            backgroundColor: riskLevelInfo.color + '20',
            borderColor: riskLevelInfo.color + '40'
          }}>
            <div className="text-xs font-semibold" style={{ color: riskLevelInfo.color }}>
              {riskLevelInfo.icon} {riskLevelInfo.title}
            </div>
            <div className="text-xs mt-1" style={{ color: riskLevelInfo.color, opacity: 0.8 }}>
              {alerts.length > 0 ? 
                `${alerts.length} active alert${alerts.length > 1 ? 's' : ''}` :
                'No active alerts'
              }
            </div>
          
            <div className="text-xs mt-1 text-gray-500">
              Updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
          
     
          {selectedSensor && (
            <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs font-semibold text-blue-900 mb-1">
                📍 Current View
              </div>
              <div className="text-xs text-blue-700 leading-tight">
                {selectedSensor.location.length > 30 
                  ? `${selectedSensor.location.slice(0, 30)}...`
                  : selectedSensor.location
                }
              </div>
              <div className="text-xs text-blue-600 font-medium mt-1">
                {selectedSensor.borough}
              </div>
            </div>
          )}
          
          {statusMessage && (
            <div
              className={`text-xs font-medium mt-2 px-2 py-1 rounded transition-all duration-300 ${
                statusMessage.type === "success"
                  ? "text-green-600 bg-green-50"
                  : statusMessage.type === "warning"
                  ? "text-orange-600 bg-orange-50"
                  : "text-red-600 bg-red-50"
              }`}
            >
              {statusMessage.type === "success" && "✅ "}
              {statusMessage.type === "warning" && "⚠️ "}
              {statusMessage.type === "error" && "❌ "}
              {statusMessage.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloodMap;